import { EventLocationData, Undefinable } from '@eagle/react-common';
import { DateTime } from 'luxon';
import { TripSummary } from './trip-history-base-view.types';
import { toEventTime } from './trip-history-event-display-utils';

interface AlertTripHistoryReferenceData {
  occurred: Date | string;
  type: 'alert';
  typeId: string;
}

interface EventTripHistoryReferenceData {
  type: 'event';
  typeId: string;
}

export type TripHistoryReferenceData = AlertTripHistoryReferenceData | EventTripHistoryReferenceData;

export const isTripHistoryReferenceData = (value: unknown): value is TripHistoryReferenceData => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<TripHistoryReferenceData>;
  if (typeof candidate.typeId !== 'string') return false;
  if (candidate.type === 'event') return true;
  return candidate.type === 'alert'
    && (candidate.occurred instanceof Date || typeof candidate.occurred === 'string');
};

export const findReferenceEvent = (
  events: EventLocationData[],
  referenceData: TripHistoryReferenceData,
): Undefinable<EventLocationData> => {
  if (referenceData.type === 'event') {
    return events.find((event) => event.eventId === referenceData.typeId);
  }

  const referenceOccurred = toEventTime(referenceData.occurred);
  if (!referenceOccurred.isValid) return;
  const referenceOccurredMillis = referenceOccurred.toMillis();

  return events.find((event) => {
    if (event.eventTypeId !== referenceData.typeId) return false;
    const eventOccurred = toEventTime(event.occurred);
    return eventOccurred.isValid && eventOccurred.toMillis() === referenceOccurredMillis;
  });
};

export const findContainingReferenceTrip = (
  trips: TripSummary[],
  event: EventLocationData,
  now: DateTime = DateTime.now(),
): Undefinable<TripSummary> => {
  const eventOccurred = toEventTime(event.occurred);
  if (!eventOccurred.isValid) return;

  return trips.find((trip) => {
    const tripEnd = trip.isOngoing ? now : trip.finish;
    return trip.entityId === event.thingId
      && eventOccurred >= trip.start
      && eventOccurred <= tripEnd;
  });
};
